/**
 * ניהול סל קניות לאורחים באמצעות Cookies
 * Guest cart management using cookies
 */

const GUEST_CART_COOKIE_NAME = 'sha_sha_guest_cart';
const COOKIE_EXPIRY_DAYS = 30;

/**
 * יצירת תאריך תפוגה עבור cookie
 */
const getExpiryDate = () => {
  const date = new Date();
  date.setTime(date.getTime() + (COOKIE_EXPIRY_DAYS * 24 * 60 * 60 * 1000));
  return date.toUTCString();
};

/**
 * שמירת סל אורחים ב-cookie
 */
export const saveGuestCart = (cartItems) => {
  try {
    const cartData = JSON.stringify(cartItems);
    document.cookie = `${GUEST_CART_COOKIE_NAME}=${encodeURIComponent(cartData)}; expires=${getExpiryDate()}; path=/; SameSite=Lax`;
    console.log('💾 Guest cart saved to cookies:', cartItems.length, 'items');
  } catch (error) {
    console.error('❌ Error saving guest cart to cookies:', error);
  }
};

/**
 * טעינת סל אורחים מ-cookie
 */
export const loadGuestCart = () => {
  try {
    const cookies = document.cookie.split(';');
    const cartCookie = cookies.find(cookie => 
      cookie.trim().startsWith(`${GUEST_CART_COOKIE_NAME}=`)
    );
    
    if (!cartCookie) {
      console.log('📭 No guest cart found in cookies');
      return [];
    }
    
    const cartData = cartCookie.split('=')[1];
    const decodedData = decodeURIComponent(cartData);
    const parsedCart = JSON.parse(decodedData);
    
    console.log('📦 Guest cart loaded from cookies:', parsedCart.length, 'items');
    return Array.isArray(parsedCart) ? parsedCart : [];
  } catch (error) {
    console.error('❌ Error loading guest cart from cookies:', error);
    return [];
  }
};

/**
 * מחיקת סל אורחים מ-cookie
 */
export const clearGuestCart = () => {
  try {
    document.cookie = `${GUEST_CART_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    console.log('🗑️ Guest cart cleared from cookies');
  } catch (error) {
    console.error('❌ Error clearing guest cart from cookies:', error);
  }
};

/**
 * הוספת מוצר לסל אורחים
 */
export const addToGuestCart = (product, quantity = 1) => {
  const currentCart = loadGuestCart();
  
  // בדיקה אם המוצר כבר קיים בסל
  const existingItemIndex = currentCart.findIndex(item => item.product._id === product._id);
  
  if (existingItemIndex >= 0) {
    // אם המוצר קיים, עדכן כמות
    currentCart[existingItemIndex].quantity += quantity;
  } else {
    // אם המוצר לא קיים, הוסף אותו
    currentCart.push({
      product: {
        _id: product._id,
        name: product.name,
        price: product.price,
        imageUrl: product.imageUrl || product.images?.[0] || '/images/placeholder.svg',
        // שמור רק את הנתונים החיוניים
        category: product.category,
        sku: product.sku
      },
      quantity: quantity,
      price: product.price
    });
  }
  
  saveGuestCart(currentCart);
  return currentCart;
};

/**
 * הסרת מוצר מסל אורחים
 */
export const removeFromGuestCart = (productId) => {
  const currentCart = loadGuestCart();
  const updatedCart = currentCart.filter(item => item.product._id !== productId);
  
  saveGuestCart(updatedCart);
  return updatedCart;
};

/**
 * עדכון כמות במוצר בסל אורחים
 */
export const updateGuestCartQuantity = (productId, quantity) => {
  const currentCart = loadGuestCart();
  
  if (quantity <= 0) {
    return removeFromGuestCart(productId);
  }
  
  const itemIndex = currentCart.findIndex(item => item.product._id === productId);
  
  if (itemIndex >= 0) {
    currentCart[itemIndex].quantity = quantity;
    saveGuestCart(currentCart);
  }
  
  return currentCart;
};

/**
 * חישוב סיכומים עבור סל אורחים
 */
export const calculateGuestCartTotals = (cartItems, coupon = null) => {
  const subtotal = cartItems.reduce((sum, item) => {
    const price = Number(item.price) || 0;
    const quantity = Number(item.quantity) || 0;
    return sum + (price * quantity);
  }, 0);
  
  let total = subtotal;
  
  if (coupon && coupon.discountPercentage) {
    const discount = subtotal * (Number(coupon.discountPercentage) / 100);
    total = Math.max(0, subtotal - discount);
  }
  
  return {
    subtotal: Number(subtotal.toFixed(2)),
    total: Number(total.toFixed(2)),
    itemsCount: cartItems.reduce((sum, item) => sum + item.quantity, 0)
  };
};

/**
 * המרת סל אורחים לפורמט של השרת
 */
export const formatGuestCartForServer = (guestCart) => {
  return guestCart.map(item => ({
    productId: item.product._id,
    quantity: item.quantity
  }));
};

/**
 * בדיקה אם יש סל אורחים
 */
export const hasGuestCart = () => {
  const cart = loadGuestCart();
  return cart.length > 0;
};
